import React, { useEffect, useState } from 'react';
import { 
    Tabs, Form, Input, Button, Space, Typography, message, Card, 
    Divider, Row, Col, Collapse, Rate, Upload, InputNumber 
} from 'antd';
import { 
    SaveOutlined, PlusOutlined, MinusCircleOutlined, UploadOutlined 
} from '@ant-design/icons';
import { 
    useGetStaticContentMutation, 
    useUpdateStaticContentMutation,
    useUploadImagesMutation 
} from '../redux/services/apiSlice';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const SingleImageUpload = ({ value, onChange, uploadMutation }) => {
    const [uploading, setUploading] = useState(false);

    const handleUpload = async ({ file }) => {
        const formData = new FormData();
        formData.append('images', file);
        setUploading(true);
        try {
            const res = await uploadMutation(formData).unwrap();
            const uploadedFile = res.files[0];
            onChange({ url: uploadedFile.url, name: uploadedFile.name });
            message.success('Image uploaded');
        } catch (err) {
            message.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            {value?.url && (
                <div style={{ position: 'relative', width: 100, height: 100, border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
                    <img src={value.url} alt="Uploaded" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <Button 
                        size="small" 
                        danger 
                        icon={<MinusCircleOutlined />} 
                        style={{ position: 'absolute', top: 2, right: 2 }}
                        onClick={() => onChange(null)}
                    />
                </div>
            )}
            <Upload 
                customRequest={handleUpload} 
                showUploadList={false}
                disabled={uploading}
                accept="image/*"
            >
                <Button icon={<UploadOutlined />} loading={uploading}>
                    {value?.url ? 'Change Image' : 'Upload Image'}
                </Button>
            </Upload>
        </Space>
    );
};


const StaticContent = () => {
    const [getStaticContent, { isLoading }] = useGetStaticContentMutation();
    const [updateStaticContent, { isLoading: isUpdating }] = useUpdateStaticContentMutation();
    const [uploadImages] = useUploadImagesMutation();
    const [form] = Form.useForm();


    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getStaticContent().unwrap();
                form.setFieldsValue(res);
            } catch (error) {
                message.error('Failed to load static content');
            }
        };
        fetchData();
    }, [getStaticContent, form]);

    const onFinish = async (values) => {
        try {
            await updateStaticContent(values).unwrap();
            message.success('Static content updated successfully');
        } catch (error) {
            message.error(error.data?.error || 'Failed to update content');
        }
    };

    const items = [
        {
            key: 'home',
            label: 'Home Page',
            children: (
                <Card title="Home Page Content" bordered={false}>
                    <Collapse defaultActiveKey={['general']}>
                        <Panel header="General Info (Contact & Social)" key="general">
                            <Form.Item name={['home', 'address']} label="Office Address">
                                <Input.TextArea rows={3} placeholder="Enter physical address" />
                            </Form.Item>
                            <Form.Item name={['home', 'contactEmail']} label="Contact Email">
                                <Input placeholder="email@example.com" />
                            </Form.Item>
                            <Form.Item name={['home', 'whatsappNumber']} label="WhatsApp Number">
                                <Input placeholder="e.g. +91 9876543210" />
                            </Form.Item>
                            
                            <Divider orientation="left">Social Media Links</Divider>
                            <Space style={{ display: 'flex', flexWrap: 'wrap' }} align="baseline">
                                <Form.Item name={['home', 'facebookUrl']} label="Facebook URL">
                                    <Input placeholder="https://facebook.com/..." style={{ width: 300 }} />
                                </Form.Item>
                                <Form.Item name={['home', 'instagramUrl']} label="Instagram URL">
                                    <Input placeholder="https://instagram.com/..." style={{ width: 300 }} />
                                </Form.Item>
                                <Form.Item name={['home', 'twitterUrl']} label="Twitter URL">
                                    <Input placeholder="https://twitter.com/..." style={{ width: 300 }} />
                                </Form.Item>
                            </Space>

                            <Divider orientation="left">Phone Numbers</Divider>
                            <Form.List name={['home', 'phoneNumbers']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name]}
                                                    rules={[{ required: true, message: 'Missing phone number' }]}
                                                >
                                                    <Input placeholder="Phone Number" style={{ width: 250 }} />
                                                </Form.Item>
                                                <MinusCircleOutlined onClick={() => remove(name)} />
                                            </Space>
                                        ))}
                                        <Form.Item>
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                Add Phone Number
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Hero Slides" key="hero">
                            <Form.List name={['home', 'hero', 'slides']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={12}>
                                                        <Form.Item {...restField} name={[name, 'title']} label="Main Title" rules={[{ required: true }]}>
                                                            <Input placeholder="Unveiling the World's" />
                                                        </Form.Item>
                                                        <Form.Item {...restField} name={[name, 'highlight']} label="Highlight Text" rules={[{ required: true }]}>
                                                            <Input placeholder="Hidden Gems" />
                                                        </Form.Item>
                                                        <Form.Item {...restField} name={[name, 'accent']} label="Accent Label">
                                                            <Input placeholder="Luxury Travel" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item {...restField} name={[name, 'image']} label="Slide Image" rules={[{ required: true }]}>
                                                            <SingleImageUpload uploadMutation={uploadImages} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item {...restField} name={[name, 'subtitle']} label="Subtitle">
                                                    <Input.TextArea rows={2} />
                                                </Form.Item>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Slide</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Featured Tours Section" key="featured">
                            <Form.Item name={['home', 'featuredTours', 'badge']} label="Badge">
                                <Input placeholder="Popular Destinations" />
                            </Form.Item>
                            <Form.Item name={['home', 'featuredTours', 'title']} label="Title">
                                <Input placeholder="Our Most Loved Tour Packages" />
                            </Form.Item>
                            <Form.Item name={['home', 'featuredTours', 'description']} label="Description">
                                <Input.TextArea rows={2} />
                            </Form.Item>
                            <Divider orientation="left">Slider Images</Divider>
                            <Form.List name={['home', 'featuredTours', 'tours']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Form.Item {...restField} name={[name, 'image']} label="Image" rules={[{ required: true }]}>
                                                    <SingleImageUpload uploadMutation={uploadImages} />
                                                </Form.Item>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Image</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Recommended Tours Section" key="recommended">
                            <Form.Item name={['home', 'recommended', 'badge']} label="Badge">
                                <Input placeholder="Recommended For You" />
                            </Form.Item>
                            <Form.Item name={['home', 'recommended', 'title']} label="Title">
                                <Input placeholder="Top Rated Experiences" />
                            </Form.Item>
                            <Form.Item name={['home', 'recommended', 'description']} label="Description">
                                <Input.TextArea rows={2} />
                            </Form.Item>
                            <Divider orientation="left">Recommended Tours</Divider>
                            <Form.List name={['home', 'recommended', 'tours']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={12}>
                                                        <Form.Item {...restField} name={[name, 'name']} label="Tour Name" rules={[{ required: true }]}>
                                                            <Input placeholder="Tour Name" />
                                                        </Form.Item>
                                                        <Form.Item {...restField} name={[name, 'price']} label="Price (₹)" rules={[{ required: true }]}>
                                                            <InputNumber style={{ width: '100%' }} min={0} />
                                                        </Form.Item>
                                                        <Form.Item {...restField} name={[name, 'rating']} label="Rating" rules={[{ required: true }]}>
                                                            <InputNumber style={{ width: '100%' }} min={0} max={5} step={0.1} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item {...restField} name={[name, 'location']} label="Location" rules={[{ required: true }]}>
                                                            <Input placeholder="Location" />
                                                        </Form.Item>
                                                        <Form.Item {...restField} name={[name, 'reviews']} label="Number of Reviews" rules={[{ required: true }]}>
                                                            <InputNumber style={{ width: '100%' }} min={0} />
                                                        </Form.Item>
                                                        <Form.Item {...restField} name={[name, 'image']} label="Image" rules={[{ required: true }]}>
                                                            <SingleImageUpload uploadMutation={uploadImages} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Tour</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="About Section" key="about">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['home', 'about', 'badge']} label="Badge">
                                        <Input placeholder="Our Mission" />
                                    </Form.Item>
                                    <Form.Item name={['home', 'about', 'title']} label="Title">
                                        <Input placeholder="Crafting Your Dream Destinations into Reality" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['home', 'about', 'image']} label="Section Image">
                                        <SingleImageUpload uploadMutation={uploadImages} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name={['home', 'about', 'description']} label="Description">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['home', 'about', 'estYear']} label="Established Year">
                                        <Input placeholder="e.g. 2010" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['home', 'about', 'experienceText']} label="Experience Text">
                                        <Input placeholder="e.g. 14+ Years of Journeys" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Divider orientation="left">Features</Divider>
                            <Form.List name={['home', 'about', 'features']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item {...restField} name={[name, 'icon']} label="Icon" rules={[{ required: true }]}>
                                                            <Input placeholder="faGlobe" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={16}>
                                                        <Form.Item {...restField} name={[name, 'title']} label="Title" rules={[{ required: true }]}>
                                                            <Input placeholder="Worldwide Hub" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item {...restField} name={[name, 'description']} label="Description">
                                                    <Input.TextArea rows={2} />
                                                </Form.Item>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Feature</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Reviews Section" key="reviews">
                            <Form.Item name={['home', 'reviews', 'badge']} label="Badge">
                                <Input placeholder="Testimonials" />
                            </Form.Item>
                            <Form.Item name={['home', 'reviews', 'title']} label="Title">
                                <Input placeholder="What Our Travelers Say" />
                            </Form.Item>
                            <Divider orientation="left">Testimonials</Divider>
                            <Form.List name={['home', 'reviews', 'testimonials']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item {...restField} name={[name, 'image']} label="User Photo">
                                                            <SingleImageUpload uploadMutation={uploadImages} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={16}>
                                                        <Row gutter={8}>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'name']} label="Name" rules={[{ required: true }]}>
                                                                    <Input />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'role']} label="Role">
                                                                    <Input placeholder="e.g. Solo Traveler" />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Form.Item {...restField} name={[name, 'rating']} label="Rating">
                                                            <Rate />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item {...restField} name={[name, 'comment']} label="Comment" rules={[{ required: true }]}>
                                                    <Input.TextArea rows={3} />
                                                </Form.Item>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Testimonial</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Newsletter Section" key="newsletter">
                            <Form.Item name={['home', 'newsletter', 'badge']} label="Badge">
                                <Input placeholder="Join Our Community" />
                            </Form.Item>
                            <Form.Item name={['home', 'newsletter', 'title']} label="Title">
                                <Input placeholder="Get Exclusive Travel Hacks & Offers" />
                            </Form.Item>
                            <Form.Item name={['home', 'newsletter', 'description']} label="Description">
                                <Input.TextArea rows={2} />
                            </Form.Item>
                            <Form.Item name={['home', 'newsletter', 'buttonText']} label="Button Text">
                                <Input placeholder="SUBSCRIBE NOW" />
                            </Form.Item>
                        </Panel>
                    </Collapse>
                </Card>
            ),
        },
        {
            key: 'tourPackages',
            label: 'Tour Packages',
            children: <Card title="Tour Packages Page" bordered={false}>Coming Soon...</Card>,
        },
        {
            key: 'aboutUs',
            label: 'About Us',
            children: (
                <Card title="About Us Page Content" bordered={false}>
                    <Collapse defaultActiveKey={['hero']}>
                        <Panel header="Hero Section" key="hero">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'hero', 'badge']} label="Badge Text">
                                        <Input placeholder="e.g. OUR STORY" />
                                    </Form.Item>
                                    <Form.Item name={['aboutUs', 'hero', 'title']} label="Hero Title">
                                        <Input placeholder="Beyond Boundaries Since 2010" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'hero', 'image']} label="Hero Image">
                                        <SingleImageUpload uploadMutation={uploadImages} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name={['aboutUs', 'hero', 'description']} label="Hero Description">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['aboutUs', 'hero', 'experienceYears']} label="Years Experience">
                                        <Input placeholder="e.g. 14+" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['aboutUs', 'hero', 'happyTravelersCount']} label="Happy Travelers">
                                        <Input placeholder="e.g. 50K+" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['aboutUs', 'hero', 'countriesCount']} label="Countries">
                                        <Input placeholder="e.g. 25+" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header="Stats Counter" key="stats">
                            <Form.List name={['aboutUs', 'stats']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={6}>
                                                        <Form.Item {...restField} name={[name, 'icon']} label="Icon (FontAwesome)" rules={[{ required: true }]}>
                                                            <Input placeholder="faGlobe" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item {...restField} name={[name, 'value']} label="Value" rules={[{ required: true }]}>
                                                            <Input placeholder="25+" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item {...restField} name={[name, 'label']} label="Label" rules={[{ required: true }]}>
                                                            <Input placeholder="Countries" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item {...restField} name={[name, 'suffix']} label="Suffix">
                                                            <Input placeholder="%" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Stat</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Mission & Vision" key="missionVision">
                            <Divider orientation="left">Mission</Divider>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'missionVision', 'missionTitle']} label="Mission Title">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'missionVision', 'missionIcon']} label="Mission Icon">
                                        <Input placeholder="faBullseye" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name={['aboutUs', 'missionVision', 'missionDescription']} label="Mission Description">
                                <Input.TextArea rows={3} />
                            </Form.Item>

                            <Divider orientation="left">Vision</Divider>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'missionVision', 'visionTitle']} label="Vision Title">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'missionVision', 'visionIcon']} label="Vision Icon">
                                        <Input placeholder="faGlobe" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name={['aboutUs', 'missionVision', 'visionDescription']} label="Vision Description">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </Panel>

                        <Panel header="Why Choose Us" key="whyChooseUs">
                            <Form.Item name={['aboutUs', 'whyChooseUs', 'badge']} label="Section Badge">
                                <Input placeholder="Why Choose Us" />
                            </Form.Item>
                            <Form.Item name={['aboutUs', 'whyChooseUs', 'title']} label="Section Title">
                                <Input placeholder="The VSP Advantage" />
                            </Form.Item>
                            <Divider orientation="left">Advantages</Divider>
                            <Form.List name={['aboutUs', 'whyChooseUs', 'advantages']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item {...restField} name={[name, 'icon']} label="Icon" rules={[{ required: true }]}>
                                                            <Input placeholder="faShieldAlt" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={16}>
                                                        <Form.Item {...restField} name={[name, 'title']} label="Title" rules={[{ required: true }]}>
                                                            <Input placeholder="Secure Travels" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item {...restField} name={[name, 'desc']} label="Description" rules={[{ required: true }]}>
                                                    <Input.TextArea rows={2} />
                                                </Form.Item>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Advantage</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Our Services" key="services">
                            <Form.Item name={['aboutUs', 'services', 'badge']} label="Section Badge">
                                <Input placeholder="What We Offer" />
                            </Form.Item>
                            <Form.Item name={['aboutUs', 'services', 'title']} label="Section Title">
                                <Input placeholder="Our Premium Services" />
                            </Form.Item>
                            <Divider orientation="left">Service Items</Divider>
                            <Form.List name={['aboutUs', 'services', 'items']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item {...restField} name={[name, 'icon']} label="Icon" rules={[{ required: true }]}>
                                                            <Input placeholder="faPlane" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={16}>
                                                        <Form.Item {...restField} name={[name, 'title']} label="Title" rules={[{ required: true }]}>
                                                            <Input placeholder="Flight Booking" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item {...restField} name={[name, 'description']} label="Description" rules={[{ required: true }]}>
                                                    <Input.TextArea rows={2} />
                                                </Form.Item>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Service</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Meet The Team" key="team">
                            <Form.Item name={['aboutUs', 'team', 'badge']} label="Section Badge">
                                <Input placeholder="Meet The Team" />
                            </Form.Item>
                            <Form.Item name={['aboutUs', 'team', 'title']} label="Section Title">
                                <Input placeholder="Behind Your Journeys" />
                            </Form.Item>
                            <Divider orientation="left">Team Members</Divider>
                            <Form.List name={['aboutUs', 'team', 'members']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item {...restField} name={[name, 'image']} label="Member Photo">
                                                            <SingleImageUpload uploadMutation={uploadImages} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={16}>
                                                        <Row gutter={8}>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'name']} label="Name" rules={[{ required: true }]}>
                                                                    <Input />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'role']} label="Role" rules={[{ required: true }]}>
                                                                    <Input />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Form.Item {...restField} name={[name, 'bio']} label="Bio">
                                                            <Input.TextArea rows={2} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Divider plain>Social Links</Divider>
                                                <Row gutter={8}>
                                                    <Col span={6}><Form.Item {...restField} name={[name, 'social', 'linkedin']} label="LinkedIn"><Input /></Form.Item></Col>
                                                    <Col span={6}><Form.Item {...restField} name={[name, 'social', 'twitter']} label="Twitter"><Input /></Form.Item></Col>
                                                    <Col span={6}><Form.Item {...restField} name={[name, 'social', 'instagram']} label="Instagram"><Input /></Form.Item></Col>
                                                    <Col span={6}><Form.Item {...restField} name={[name, 'social', 'facebook']} label="Facebook"><Input /></Form.Item></Col>
                                                </Row>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Team Member</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Testimonials" key="testimonials">
                            <Form.Item name={['aboutUs', 'testimonials', 'badge']} label="Section Badge">
                                <Input placeholder="Testimonials" />
                            </Form.Item>
                            <Form.Item name={['aboutUs', 'testimonials', 'title']} label="Section Title">
                                <Input placeholder="What Travelers Say" />
                            </Form.Item>
                            <Divider orientation="left">Testimonial Items</Divider>
                            <Form.List name={['aboutUs', 'testimonials', 'items']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={6}>
                                                        <Form.Item {...restField} name={[name, 'image']} label="User Photo">
                                                            <SingleImageUpload uploadMutation={uploadImages} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={18}>
                                                        <Row gutter={8}>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'name']} label="Name" rules={[{ required: true }]}>
                                                                    <Input />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'location']} label="Location">
                                                                    <Input />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Row gutter={8}>
                                                            <Col span={8}>
                                                                <Form.Item {...restField} name={[name, 'rating']} label="Rating">
                                                                    <Rate />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Form.Item {...restField} name={[name, 'text']} label="Review text" rules={[{ required: true }]}>
                                                            <Input.TextArea rows={2} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Testimonial</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Milestones" key="milestones">
                            <Form.Item name={['aboutUs', 'milestones', 'badge']} label="Section Badge">
                                <Input placeholder="Our Journey" />
                            </Form.Item>
                            <Form.Item name={['aboutUs', 'milestones', 'title']} label="Section Title">
                                <Input placeholder="Milestones Achieved" />
                            </Form.Item>
                            <Divider orientation="left">Timeline Items</Divider>
                            <Form.List name={['aboutUs', 'milestones', 'items']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={4}>
                                                        <Form.Item {...restField} name={[name, 'year']} label="Year" rules={[{ required: true }]}>
                                                            <InputNumber style={{ width: '100%' }} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={20}>
                                                        <Form.Item {...restField} name={[name, 'title']} label="Title" rules={[{ required: true }]}>
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item {...restField} name={[name, 'description']} label="Description">
                                                    <Input.TextArea rows={2} />
                                                </Form.Item>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Milestone</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>

                        <Panel header="Contact Section (Footer CTA)" key="contact">
                            <Form.Item name={['aboutUs', 'contact', 'title']} label="Title">
                                <Input placeholder="Ready to Start Your Journey?" />
                            </Form.Item>
                            <Form.Item name={['aboutUs', 'contact', 'subtitle']} label="Subtitle">
                                <Input.TextArea rows={2} />
                            </Form.Item>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'contact', 'buttonText']} label="Button Text">
                                        <Input placeholder="Contact Us" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['aboutUs', 'contact', 'phone']} label="Phone Number">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                </Card>
            ),
        },
        {
            key: 'contactUs',
            label: 'Contact Us',
            children: <Card title="Contact Us Page" bordered={false}>Coming Soon...</Card>,
        },
        {
            key: 'showcase',
            label: 'Showcase',
            children: (
                <Card title="Showcase Page Content" bordered={false}>
                    <Collapse defaultActiveKey={['metadata']}>
                        <Panel header="Page Metadata" key="metadata">
                            <Form.Item name={['showcase', 'title']} label="Page Title">
                                <Input placeholder="Travel Showcase" />
                            </Form.Item>
                            <Form.Item name={['showcase', 'subtitle']} label="Page Subtitle">
                                <Input.TextArea rows={2} placeholder="Capturing moments that define a lifetime..." />
                            </Form.Item>
                        </Panel>

                        <Panel header="Showcase Items" key="items">
                            <Form.List name={['showcase', 'items']}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Card size="small" style={{ marginBottom: 16 }} key={key}
                                                extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                            >
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Form.Item {...restField} name={[name, 'image']} label="Gallery Image" rules={[{ required: true }]}>
                                                            <SingleImageUpload uploadMutation={uploadImages} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={16}>
                                                        <Row gutter={8}>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'customer']} label="Customer Name" rules={[{ required: true }]}>
                                                                    <Input placeholder="Vikram & Family" />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'location']} label="Location" rules={[{ required: true }]}>
                                                                    <Input placeholder="Switzerland" />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Row gutter={8}>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'package']} label="Tour Package" rules={[{ required: true }]}>
                                                                    <Input placeholder="Swiss Alpine Luxury" />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'duration']} label="Duration" rules={[{ required: true }]}>
                                                                    <Input placeholder="7D 6N" />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Row gutter={8}>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'date']} label="Travel Date" rules={[{ required: true }]}>
                                                                    <Input placeholder="Jan 2024" />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item {...restField} name={[name, 'rating']} label="Rating">
                                                                    <Rate />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                                <Form.Item {...restField} name={[name, 'testimonial']} label="Customer Testimonial" rules={[{ required: true }]}>
                                                    <Input.TextArea rows={3} placeholder="Share their story..." />
                                                </Form.Item>
                                                <Divider plain>Tags</Divider>
                                                <Form.List name={[name, 'tags']}>
                                                    {(tagFields, { add: addTag, remove: removeTag }) => (
                                                        <>
                                                            <Row gutter={8}>
                                                                {tagFields.map((tagField, tagIdx) => (
                                                                    <Col span={6} key={tagField.key}>
                                                                        <Form.Item {...tagField} rules={[{ required: true }]}>
                                                                            <Input 
                                                                                placeholder="Tag" 
                                                                                suffix={<MinusCircleOutlined onClick={() => removeTag(tagIdx)} />}
                                                                            />
                                                                        </Form.Item>
                                                                    </Col>
                                                                ))}
                                                                <Col span={6}>
                                                                    <Button type="dashed" onClick={() => addTag()} block icon={<PlusOutlined />}>Add Tag</Button>
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}
                                                </Form.List>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Showcase Item</Button>
                                    </>
                                )}
                            </Form.List>
                        </Panel>
                    </Collapse>
                </Card>
            ),
        },
        {
            key: 'customTours',
            label: 'Custom Tours',
            children: <Card title="Custom Tours Page" bordered={false}>Coming Soon...</Card>,
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <Title level={2}>Static Content Management</Title>
                <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    loading={isUpdating || isLoading} 
                    onClick={() => form.submit()}
                >
                    Save All Changes
                </Button>
            </div>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ home: { phoneNumbers: [] } }}
            >
                <Tabs defaultActiveKey="home" items={items} />
            </Form>
        </div>
    );
};

export default StaticContent;
